/**
 * DailyLogTab — Project-specific Daily Log view
 * Shows all daily logs for a project, published or internal.
 * Management can see all; clients see only published ones via ClientDailyLogEmbed.
 *
 * Client sharing uses the same PrepareDailyLogForClientModal + dailyLogClientShare
 * path as the management side-menu Daily Log page.
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { formatDate } from '@/lib/dateUtils';
import PhotoLightbox, { type LightboxPhoto } from '@/components/feature/PhotoLightbox';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';
import PrepareDailyLogForClientModal from '@/components/feature/PrepareDailyLogForClientModal';
import CreateDailyLogModal, { type NewUploadFile, type DailyLogInitialValues } from '@/pages/management/daily-log/components/CreateDailyLogModal';
import { useDailyLogCreate } from '@/hooks/useDailyLogCreate';
import { useAuth } from '@/hooks/useAuth';
import { type DailyLogReviewPhoto } from '@/hooks/useManagementDailyLog';
import { DAILY_LOG_SYNC_EVENT, emitDailyLogSync } from '@/lib/dailyLogUtils';
import { normalizeDailyLogId } from '@/lib/dailyLogShareDiagnostics';

const PROJECT_FILE_SELECT =
  'id, project_id, file_name, file_url, file_type, file_size, log_date, caption, stage_id, review_status, is_client_visible, is_visible, is_deleted, note, created_at, uploaded_by, daily_log_id, storage_bucket, storage_path';

interface DailyLogEntry {
  id: string;
  project_id: string;
  log_date: string;
  title: string | null;
  management_comment: string | null;
  internal_note: string | null;
  visibility: string;
  is_published_to_client: boolean;
  published_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string | null;
}

interface DailyLogPhoto {
  id: string;
  daily_log_id: string;
  photo_url: string;
  caption: string | null;
}

interface Props {
  projectId: string;
  viewerRole?: 'management' | 'client';
}

function fmtDate(d: string): string {
  return new Date(d + 'T00:00:00').toLocaleDateString('en-NZ', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function timeAgoShort(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function DailyLogTab({ projectId, viewerRole = 'management' }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createDailyLog, updateDailyLog, creating: saving } = useDailyLogCreate();

  const [logs, setLogs] = useState<DailyLogEntry[]>([]);
  const [photosMap, setPhotosMap] = useState<Record<string, DailyLogPhoto[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [lightboxPhotos, setLightboxPhotos] = useState<LightboxPhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editLogId, setEditLogId] = useState<string | undefined>(undefined);
  const [editInitialValues, setEditInitialValues] = useState<DailyLogInitialValues | undefined>(undefined);

  // Delete state
  const [deleteLogId, setDeleteLogId] = useState<string | null>(null);
  const [deleteLogTitle, setDeleteLogTitle] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Canonical Prepare-for-Client modal (same as side-menu Daily Log)
  const [prepareClientOpen, setPrepareClientOpen] = useState(false);
  const [prepareClientContext, setPrepareClientContext] = useState<{
    projectId: string;
    projectName: string;
    logDate: string;
    dateLabel: string;
    photos: DailyLogReviewPhoto[];
  } | null>(null);
  const [prepareLoadingLogId, setPrepareLoadingLogId] = useState<string | null>(null);
  const [prepareError, setPrepareError] = useState('');

  const [projectName, setProjectName] = useState('');

  useEffect(() => {
    if (!projectId) return;
    supabase
      .from('jphph_projects')
      .select('project_name')
      .eq('id', projectId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) setProjectName((data as { project_name: string }).project_name);
      });
  }, [projectId]);

  function openCreateModal() {
    setModalMode('create');
    setEditLogId(undefined);
    setEditInitialValues(undefined);
    setShowModal(true);
  }

  function openEditModal(log: DailyLogEntry) {
    setModalMode('edit');
    setEditLogId(log.id);
    setEditInitialValues({
      logId: log.id,
      projectId: log.project_id,
      logDate: log.log_date,
      title: log.title || '',
      managementComment: log.management_comment || '',
      internalNote: log.internal_note || '',
      visibility: (log.visibility as 'internal' | 'client') || 'client',
      isPublishedToClient: log.is_published_to_client,
    });
    setShowModal(true);
  }

  async function handleCreateDailyLog(payload: {
    input: Parameters<typeof createDailyLog>[0]['input'];
    newFiles: NewUploadFile[];
    existingPhotoIds: string[];
  }) {
    try {
      const result = await createDailyLog({
        input: payload.input,
        newFiles: payload.newFiles,
        existingPhotoIds: payload.existingPhotoIds,
      });
      if (result.success) {
        setShowModal(false);
        load();
      }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { success: false, error: msg };
    }
  }

  async function handleEditDailyLog(payload: {
    input: Parameters<typeof updateDailyLog>[0]['input'] & { logId?: string };
    newFiles: NewUploadFile[];
  }) {
    try {
      const resolvedEditId = normalizeDailyLogId(editLogId) ?? normalizeDailyLogId(payload.input.logId);
      if (!resolvedEditId) {
        const msg = 'Daily Log ID could not be resolved. The update was not sent.';
        console.warn('[DailyLogTab] handleEditDailyLog blocked: invalid editLogId', {
          editLogId,
          payloadLogId: payload.input.logId,
          projectId,
        });
        return { success: false, error: msg };
      }

      const { logId: _ignoredLogId, ...inputFields } = payload.input;
      const result = await updateDailyLog({
        dailyLogId: resolvedEditId,
        input: inputFields,
        newFiles: payload.newFiles,
        existingPhotoIds: [],
        removedLogPhotoIds: [],
      });
      if (result.success) {
        setShowModal(false);
        setEditLogId(undefined);
        setEditInitialValues(undefined);
        load();
      }
      return result;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      return { success: false, error: msg };
    }
  }

  async function loadPhotosForClientShare(log: DailyLogEntry): Promise<DailyLogReviewPhoto[]> {
    const validLogId = normalizeDailyLogId(log.id);
    const byId = new Map<string, DailyLogReviewPhoto>();

    const { data: byDateRows, error: byDateErr } = await supabase
      .from('jphph_project_files')
      .select(PROJECT_FILE_SELECT)
      .eq('project_id', projectId)
      .eq('log_date', log.log_date)
      .eq('is_deleted', false)
      .or('uploaded_context.eq.daily_log,uploaded_context.is.null')
      .in('file_type', ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'])
      .order('created_at', { ascending: false });

    if (byDateErr) throw byDateErr;

    for (const row of (byDateRows ?? []) as DailyLogReviewPhoto[]) {
      byId.set(row.id, {
        ...row,
        stage_name: null,
        daily_log_id: normalizeDailyLogId(row.daily_log_id) ?? validLogId,
      });
    }

    if (validLogId) {
      const { data: byLogRows, error: byLogErr } = await supabase
        .from('jphph_project_files')
        .select(PROJECT_FILE_SELECT)
        .eq('project_id', projectId)
        .eq('daily_log_id', validLogId)
        .eq('is_deleted', false)
        .or('uploaded_context.eq.daily_log,uploaded_context.is.null')
        .in('file_type', ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'])
        .order('created_at', { ascending: false });

      if (byLogErr) throw byLogErr;

      for (const row of (byLogRows ?? []) as DailyLogReviewPhoto[]) {
        if (!byId.has(row.id)) {
          byId.set(row.id, {
            ...row,
            stage_name: null,
            daily_log_id: normalizeDailyLogId(row.daily_log_id) ?? validLogId,
          });
        }
      }
    }

    return Array.from(byId.values());
  }

  async function openPrepareForClient(log: DailyLogEntry) {
    setPrepareError('');
    setPrepareLoadingLogId(log.id);
    try {
      const photos = await loadPhotosForClientShare(log);
      setPrepareClientContext({
        projectId,
        projectName: projectName || 'Current Project',
        logDate: log.log_date,
        dateLabel: fmtDate(log.log_date),
        photos,
      });
      setPrepareClientOpen(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn('[DailyLogTab] openPrepareForClient failed', {
        projectId,
        logDate: log.log_date,
        logId: log.id,
        error: msg,
      });
      setPrepareError(msg || 'Failed to load photos for client share');
    } finally {
      setPrepareLoadingLogId(null);
    }
  }

  async function confirmDeleteLog() {
    const archiveId = normalizeDailyLogId(deleteLogId);
    if (!archiveId) {
      setDeleteError('Daily Log ID could not be resolved. The archive was not sent.');
      return;
    }
    setDeleting(true);
    setDeleteError('');
    try {
      const { error: err } = await supabase
        .from('jphph_daily_logs')
        .update({
          is_deleted: true,
          deleted_at: new Date().toISOString(),
          deleted_by: user?.id || null,
        })
        .eq('id', archiveId);
      if (err) throw err;
      setDeleteLogId(null);
      load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setDeleteError(msg || 'Failed to archive Daily Log');
    } finally {
      setDeleting(false);
    }
  }

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      let query = supabase
        .from('jphph_daily_logs')
        .select(
          'id, project_id, log_date, title, management_comment, internal_note, visibility, is_published_to_client, published_at, created_by, created_at, updated_at'
        )
        .eq('project_id', projectId)
        .eq('is_deleted', false)
        .order('log_date', { ascending: false })
        .order('created_at', { ascending: false });

      // Clients only see published logs
      if (viewerRole === 'client') {
        query = query.eq('is_published_to_client', true);
      }

      const { data: logRows, error: logErr } = await query;
      if (logErr) throw logErr;

      const logList = (logRows || []) as DailyLogEntry[];
      setLogs(logList);

      if (logList.length === 0) {
        setPhotosMap({});
        setLoading(false);
        return;
      }

      const logIds = logList.map((l) => l.id).filter((id) => normalizeDailyLogId(id));
      if (logIds.length === 0) {
        setPhotosMap({});
        setLoading(false);
        return;
      }

      const { data: photoRows, error: photoErr } = await supabase
        .from('jphph_daily_log_photos')
        .select('id, daily_log_id, photo_url, caption, source_photo_id')
        .in('daily_log_id', logIds)
        .order('created_at', { ascending: true });

      if (photoErr) {
        console.error('[DailyLogTab] Photo fetch error:', photoErr);
      }

      const rawPhotos = photoRows || [];

      const map: Record<string, DailyLogPhoto[]> = {};
      for (const p of rawPhotos as { id: string; daily_log_id: string; photo_url: string | null; caption: string | null }[]) {
        const url = p.photo_url || '';
        if (!url) continue;
        if (!map[p.daily_log_id]) map[p.daily_log_id] = [];
        map[p.daily_log_id].push({ id: p.id, daily_log_id: p.daily_log_id, photo_url: url, caption: p.caption });
      }
      setPhotosMap(map);
    } catch (e) {
      console.error('[DailyLogTab] Load error:', e);
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, viewerRole]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (!detail?.projectId || detail.projectId === projectId) {
        void load();
      }
    };
    window.addEventListener(DAILY_LOG_SYNC_EVENT, handler);
    return () => window.removeEventListener(DAILY_LOG_SYNC_EVENT, handler);
  }, [load, projectId]);

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const openLightbox = (photos: DailyLogPhoto[], idx: number) => {
    setLightboxPhotos(photos.map((p) => ({ url: p.photo_url, caption: p.caption || '' })));
    setLightboxIndex(idx);
  };

  const sharedModals = (
    <>
      {prepareError && (
        <div className="fixed bottom-4 left-1/2 z-[80] -translate-x-1/2 max-w-md px-4 py-3 rounded-xl bg-[#FEF2F2] border border-[#FECACA] shadow-lg">
          <p className="text-[#EF4444] text-xs font-medium">{prepareError}</p>
        </div>
      )}

      <CreateDailyLogModal
        open={showModal}
        onClose={() => {
          setShowModal(false);
          if (modalMode === 'edit') {
            setEditLogId(undefined);
            setEditInitialValues(undefined);
            setModalMode('create');
          }
        }}
        mode={modalMode}
        editLogId={editLogId}
        initialValues={editInitialValues}
        fixedProjectId={projectId}
        projects={[{ id: projectId, project_name: projectName || 'Current Project' }]}
        existingPhotos={[]}
        onSubmit={handleCreateDailyLog}
        onSubmitEdit={handleEditDailyLog}
        loading={saving}
      />

      {prepareClientOpen && prepareClientContext && user && (
        <PrepareDailyLogForClientModal
          open={prepareClientOpen}
          projectId={prepareClientContext.projectId}
          projectName={prepareClientContext.projectName}
          logDate={prepareClientContext.logDate}
          dateLabel={prepareClientContext.dateLabel}
          photos={prepareClientContext.photos}
          pmUserId={user.id}
          onClose={() => {
            setPrepareClientOpen(false);
            setPrepareClientContext(null);
          }}
          onSuccess={() => {
            setPrepareClientOpen(false);
            setPrepareClientContext(null);
            void load();
            emitDailyLogSync(projectId);
          }}
          onPhotosChanged={() => {
            void load();
            emitDailyLogSync(projectId);
          }}
        />
      )}
    </>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <i className="ri-loader-4-line animate-spin text-[#E8621A] text-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[#FEF2F2] rounded-xl p-4 text-center">
        <p className="text-[#EF4444] text-sm">{error}</p>
        <button type="button" onClick={load} className="mt-2 text-[#EF4444] text-xs font-semibold cursor-pointer">
          Retry
        </button>
      </div>
    );
  }

  if (logs.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
          <i className="ri-book-open-line text-5xl" />
        </div>
        <p className="text-[#374151] font-semibold text-sm">No Daily Logs yet</p>
        <p className="text-[#9CA3AF] text-xs mt-1 max-w-xs mx-auto">
          {viewerRole === 'management'
            ? 'Create a Daily Log from the Daily Log Review page to document site progress with photos.'
            : 'Your site manager will share daily updates here once they are published.'}
        </p>
        {viewerRole === 'management' && (
          <>
            <button
              type="button"
              onClick={openCreateModal}
              className="mt-4 px-5 py-2.5 rounded-xl bg-[#059669] text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line mr-1" />
              Create Daily Log
            </button>
            <button
              type="button"
              onClick={() => navigate('/management/daily-log')}
              className="mt-2 px-5 py-2.5 rounded-xl bg-[#E8621A] text-white text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-line mr-1" />
              Go to Daily Log Review
            </button>
          </>
        )}
        {sharedModals}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {lightboxIndex !== null && lightboxPhotos.length > 0 && (
        <PhotoLightbox
          photos={lightboxPhotos}
          initialIndex={lightboxIndex}
          onClose={() => { setLightboxIndex(null); setLightboxPhotos([]); }}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <i className="ri-book-open-line text-[#059669] text-base" />
          <span className="text-sm font-bold text-[#1A1A1A]">Daily Logs</span>
          <span className="px-2 py-0.5 bg-[#F0FDF4] text-[#059669] text-[10px] font-bold rounded-full">{logs.length}</span>
        </div>
        {viewerRole === 'management' && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openCreateModal}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[#059669] text-white text-xs font-semibold cursor-pointer hover:bg-[#047857] transition-colors whitespace-nowrap"
            >
              <i className="ri-add-line" />
              Create Daily Log
            </button>
            <button
              type="button"
              onClick={() => navigate('/management/daily-log')}
              className="flex items-center gap-1.5 text-xs font-semibold text-[#E8621A] hover:text-[#B84C10] cursor-pointer whitespace-nowrap"
            >
              <i className="ri-external-link-line" />
              Manage Daily Logs
            </button>
          </div>
        )}
      </div>

      {/* Log entries */}
      {logs.map((log) => {
        const photos = photosMap[log.id] || [];
        const isExpanded = expandedIds.has(log.id);

        return (
          <div key={log.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            {/* Entry header */}
            <button
              type="button"
              onClick={() => toggleExpand(log.id)}
              className="w-full text-left px-4 py-3 border-b border-gray-50 flex items-start gap-3 cursor-pointer hover:bg-[#FAFAFA] transition-colors"
            >
              <div className="w-8 h-8 rounded-xl bg-[#F0FDF4] flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-calendar-line text-[#059669] text-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-bold text-[#1A1A1A]">
                    {log.title || fmtDate(log.log_date)}
                  </span>
                  {log.is_published_to_client ? (
                    <span className="px-1.5 py-0.5 bg-[#F0FDF4] text-[#059669] text-[9px] font-bold rounded-full whitespace-nowrap">
                      <i className="ri-eye-line mr-0.5" />Client Visible
                    </span>
                  ) : (
                    <span className="px-1.5 py-0.5 bg-[#F3F4F6] text-[#374151] text-[9px] font-bold rounded-full whitespace-nowrap">
                      <i className="ri-eye-off-line mr-0.5" />Internal Only
                    </span>
                  )}
                  {photos.length > 0 && (
                    <span className="px-1.5 py-0.5 bg-[#FEF0E7] text-[#E8621A] text-[9px] font-bold rounded-full whitespace-nowrap">
                      <i className="ri-image-line mr-0.5" />{photos.length} photo{photos.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[10px] text-[#9CA3AF]">{formatDate(log.log_date)}</span>
                  <span className="text-[10px] text-[#D1D5DB]">·</span>
                  <span className="text-[10px] text-[#9CA3AF]">{timeAgoShort(log.updated_at || log.created_at)}</span>
                </div>
              </div>
              {isExpanded ? (
                <i className="ri-arrow-up-s-line text-[#9CA3AF] text-lg flex-shrink-0" />
              ) : (
                <i className="ri-arrow-down-s-line text-[#9CA3AF] text-lg flex-shrink-0" />
              )}
            </button>

            {/* Expanded content */}
            {isExpanded && (
              <div className="p-4 space-y-3">
                {log.management_comment && (
                  <div>
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-1">Site Update</p>
                    <p className="text-sm text-[#374151] leading-relaxed">{log.management_comment}</p>
                  </div>
                )}

                {viewerRole === 'management' && log.internal_note && (
                  <div className="bg-[#FFF7ED] rounded-xl p-3">
                    <p className="text-[10px] font-bold text-[#F97316] uppercase tracking-wider mb-1">
                      <i className="ri-lock-line mr-0.5" />Internal Note
                    </p>
                    <p className="text-sm text-[#374151] leading-relaxed">{log.internal_note}</p>
                  </div>
                )}

                {photos.length > 0 && (
                  <div>
                    <p className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider mb-2">
                      Site Photos ({photos.length})
                    </p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {photos.map((photo, idx) => (
                        <button
                          type="button"
                          key={photo.id}
                          onClick={() => openLightbox(photos, idx)}
                          className="aspect-[4/3] rounded-xl overflow-hidden cursor-pointer group relative"
                        >
                          <ProjectPhotoImage
                            src={photo.photo_url}
                            alt={photo.caption || 'Site photo'}
                            sizes="(max-width: 640px) 45vw, 280px"
                            loading="lazy"
                            debugContext="DailyLogTab"
                          />
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                            <i className="ri-zoom-in-line text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </button>
                      ))}
                    </div>
                    {photos.some((p) => p.caption) && (
                      <div className="mt-2 space-y-1">
                        {photos.filter((p) => p.caption).map((p) => (
                          <div key={p.id} className="flex items-start gap-1.5">
                            <i className="ri-chat-1-line text-[#9CA3AF] text-xs mt-0.5 flex-shrink-0" />
                            <p className="text-[#6B7280] text-xs leading-relaxed">{p.caption}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {viewerRole === 'management' && (
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-50 flex-wrap">
                    <button
                      type="button"
                      onClick={() => openEditModal(log)}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#374151] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
                    >
                      <i className="ri-edit-line" /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => void openPrepareForClient(log)}
                      disabled={prepareLoadingLogId === log.id}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#059669] text-white text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#047857] transition-colors disabled:opacity-50"
                    >
                      {prepareLoadingLogId === log.id ? (
                        <i className="ri-loader-4-line animate-spin" />
                      ) : (
                        <i className="ri-share-forward-line" />
                      )}
                      Prepare for Client
                    </button>
                    <button
                      type="button"
                      onClick={() => { setDeleteLogId(log.id); setDeleteLogTitle(log.title || fmtDate(log.log_date)); setDeleteError(''); }}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-[#EF4444] text-[10px] font-bold cursor-pointer whitespace-nowrap hover:bg-[#FEF2F2] transition-colors"
                    >
                      <i className="ri-delete-bin-line" /> Archive
                    </button>
                  </div>
                )}

                <div className="pt-2 border-t border-gray-50 flex items-center justify-between text-[10px] text-[#9CA3AF]">
                  <span>
                    {log.is_published_to_client && log.published_at ? (
                      <>Published {timeAgoShort(log.published_at)}</>
                    ) : (
                      <>Created {timeAgoShort(log.created_at)}</>
                    )}
                  </span>
                  {viewerRole === 'management' && (
                    <span className={`px-2 py-0.5 rounded-full font-bold ${
                      log.visibility === 'client'
                        ? 'bg-[#F0FDF4] text-[#059669]'
                        : 'bg-[#F3F4F6] text-[#374151]'
                    }`}>
                      {log.visibility === 'client' ? 'Shared with client' : 'Internal'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}

      {deleteLogId && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteLogId(null)} />
          <div className="relative bg-white w-full max-w-sm rounded-2xl p-6 text-center">
            <div className="w-12 h-12 rounded-2xl bg-[#FEF2F2] flex items-center justify-center mx-auto mb-3">
              <i className="ri-archive-line text-[#EF4444] text-2xl" />
            </div>
            <p className="text-[#1A1A1A] font-bold text-base mb-1">Archive Daily Log</p>
            <p className="text-[#6B7280] text-sm mb-1">
              "{deleteLogTitle}" will be archived and hidden from active lists.
            </p>
            {deleteError && (
              <div className="flex items-start gap-2 bg-[#FEF2F2] rounded-xl px-3 py-2.5 mt-3 text-left">
                <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0 mt-0.5" />
                <p className="text-[#EF4444] text-xs">{deleteError}</p>
              </div>
            )}
            <div className="flex gap-3 mt-5">
              <button type="button" onClick={() => setDeleteLogId(null)} className="flex-1 py-3 rounded-xl bg-gray-100 text-[#374151] text-sm font-semibold cursor-pointer whitespace-nowrap">
                Cancel
              </button>
              <button type="button" onClick={confirmDeleteLog} disabled={deleting} className="flex-1 py-3 rounded-xl bg-[#EF4444] text-white text-sm font-semibold cursor-pointer whitespace-nowrap disabled:opacity-50">
                {deleting ? <i className="ri-loader-4-line animate-spin" /> : 'Archive'}
              </button>
            </div>
          </div>
        </div>
      )}

      {sharedModals}
    </div>
  );
}
